import SplitText from "@Components/SplitText";
import Agreement from "@Components/agreement";
import SendVerificationCode from "@Components/ask/SendVerificationCode";
import { loginAtom } from "@Lib/jotai/login";
import { initTracking } from "@Lib/jotai/tracking";
import { fetchNewCarBrands, fetchNewCarKinds, fetchNewCarModel } from "@Services/buyCar";
import { postMenu } from "@Services/buyCarMenu";
import { getRegions } from "@Services/common";
import { EBrandSelectType } from "@Types/constants";
import { CheckedFillIcon, RoundIcon } from "@t8891/icon";
import { useSetState } from "ahooks";
import { Form, Input, Select, message } from "antd";
import dayjs from "dayjs";
import { useAtom } from "jotai";
import debounce from "lodash/debounce";
import qs from "qs";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useQuery } from "react-query";
import RegionSelect from "../RegionSelect";

const SeekFormComp: React.FC<{ data: any; onPageChange: (data: any, kindId: string) => void; id?: string }> = ({
  data,
  onPageChange,
  id,
}) => {
  const { source } = qs.parse(location.search, { ignoreQueryPrefix: true }) as { source: string };
  const [formState, setFormState] = useSetState({
    insuranceText: "",
    returnFeeText: "",
    equipmentText: "",
    remarkText: "",
    colorText: "",
    activityStatus: undefined,
  });
  const [loginInfo, updateLoginInfo] = useAtom(loginAtom);
  const [tracking] = useAtom(initTracking);

  const {
    control,
    setValue,
    getValues,
    trigger,
    register,
    formState: { errors },
  } = useForm({
    defaultValues: {
      brandId: undefined,
      kindId: undefined,
      type: 2,
      myid: undefined,
      color: undefined,
      otherColor: "",
      buyCarTime: undefined,
      regionId: undefined,
      price: "",
      paymentType: "",
      menuDetail: [] as any[],
      mobile: "",
      code: "",
      agree: 0,
      codeVerify: 1,
      name: "",
    },
  });

  const {
    brandId,
    kindId,
    myid,
    color,
    buyCarTime,
    regionId,
    price,
    paymentType,
    menuDetail,
    mobile,
    code,
    codeVerify,
  } = getValues();
  const { insuranceText, returnFeeText, equipmentText, remarkText, colorText } = formState;

  // 獲取廠牌數據
  const { data: brandData } = useQuery("fetchNewCarBrands", async () => {
    const response = await fetchNewCarBrands({
      type: EBrandSelectType.INQUIRY,
    });
    if (response.status === 12000 && response.data) {
      const current = [] as any[];
      response.data?.brand?.forEach((item) => {
        if (item?.list) {
          item.list?.forEach((i) => {
            current.push(i);
          });
        }
      });
      return current;
    }
    return [];
  });
  // 獲取车款數據
  const { data: kindData } = useQuery(
    ["fetchNewCarKinds", brandId],
    async () => {
      const response = await fetchNewCarKinds({
        type: EBrandSelectType.INQUIRY,
        b: Number(brandId),
      });
      if (response.status === 12000 && response.data) {
        return response.data.data;
      }
      return undefined;
    },
    {
      enabled: !!brandId,
    },
  );

  // 獲取車型數據
  const { data: modelData } = useQuery(
    ["fetchNewCarModel", kindId],
    async () => {
      const response = await fetchNewCarModel({
        type: EBrandSelectType.INQUIRY_KIND,
        k: Number(kindId),
      });
      if (response.status === 12000 && response.data) {
        return response.data.list;
      }

      return undefined;
    },
    {
      enabled: !!kindId,
    },
  );

  useEffect(() => {
    trigger();
    try {
      const inquiryData = localStorage.getItem("inquiryData")
        ? JSON.parse(localStorage.getItem("inquiryData") || "")
        : {};
      if (inquiryData?.mobile) {
        setValue("mobile", inquiryData.mobile);
        setValue("codeVerify", inquiryData.codeVerify);
      }
    } catch (e) {
      console.log(e);
    }
  }, []);

  useEffect(() => {
    // 解决时序问题
    if (loginInfo && !mobile) {
      setValue("mobile", loginInfo?.data.m_mobile);
      setValue("codeVerify", 0);
    }
  }, [loginInfo]);

  useEffect(() => {
    if (data?.menu) {
      const v = data.menu;
      if (v) {
        setValue("brandId", v.brandId || undefined);
        setValue("kindId", v.kindId || undefined);
        setValue("myid", v.myid || undefined);
        setValue("color", v.color || undefined);
        setValue("otherColor", v.otherColor);
        if (!!v.mobile) {
          setValue("mobile", v.mobile);
        }
        setValue("buyCarTime", v.buyCarTime || undefined);
        setValue("regionId", v.region);
        setValue("price", v.price);
        setValue("paymentType", v.paymentType);
        setValue("name", v.name);
        const currentOtherOptions = [] as number[];
        Object.entries(v.otherOptions || {})?.forEach(([key, value]) => {
          currentOtherOptions.push(Number(key));
        });

        setValue("menuDetail", currentOtherOptions);
        setFormState({
          colorText: v.otherColor,
          insuranceText: v?.otherOptions?.["1"],
          returnFeeText: v?.otherOptions?.["2"],
          equipmentText: v?.otherOptions?.["3"],
          remarkText: v?.otherOptions?.["4"],
        });
        trigger();
      }
    }
  }, [data?.menu]);

  const onSubmit = async (data: any) => {
    const { source: querySource } = qs.parse(window.location.search, { ignoreQueryPrefix: true }) as {
      source: string;
    };
    const params = {
      ...data,
      menuDetail: JSON.stringify(
        [
          { name: "保險", value: 1, content: insuranceText },
          { name: "規費合計", value: 2, content: returnFeeText },
          { name: "配備", value: 3, content: equipmentText },
          { name: "備註", value: 4, content: remarkText },
        ].filter((i: any) => i.content !== undefined),
      ),
      source: source ? decodeURIComponent(source) : "",
      entrance: querySource || "",
      followId: id ?? 0,
    };

    const response = await postMenu(params as any);
    if (response.status === 12000) {
      let localInquiryData = {
        name: data.name,
        mobile: data.mobile,
        agencyAgreement: data.agree,
        codeVerify: 0,
        regionId: data.regionId,
        lastModify_Date: dayjs().format("YYYY/MM/DD HH:mm:ss"),
      };
      if (!!response.data?.inquiryId) {
        tracking.trackEvent({
          name: "inquiry_new_success",
          params: {
            page_sub_business: "新車線索",
            page_business: "新車",
            trigger_id: "3341",
            page_id: "640",
            trigger_name: "徵求菜單詢價成功",
            next_spm: "51.640.567.1698",
            page: "徵求菜單資料填寫頁",
            channel: "買新車",
            element_id: "徵求菜單詢價成功",
            element: "按鈕",
            module: "底部",
            tracker_method: "config",
            source: source ? decodeURIComponent(source) : "",
            inquiry_id: response?.data?.inquiryId,
            inquiry_source: source ? decodeURIComponent(source) : "",
          },
        });
      }

      localStorage.setItem("inquiryData", JSON.stringify(localInquiryData));
      onPageChange?.(response.data, data.kindId);
    } else {
      message.error(response.data?.message || "提交失敗");
    }
  };

  // 獲取地區數據
  const { data: regionsData } = useQuery(["getRegions"], async () => {
    const response = await getRegions({ type: "all" });
    const resdata = response?.data as any;
    if (response.status === 12000 && resdata) {
      return resdata;
    }
    return [];
  });

  return (
    <Form labelCol={{ span: 6 }} wrapperCol={{ span: 19 }} className='w-full mt-20'>
      <div className='flex flex-col mt-20 rounded-4 bg-[#fff]'>
        <span className='font-semibold text-20 text-[#333] my-30 ml-[150px]'>1.車輛資訊</span>
        <Form.Item
          {...register("kindId", {
            required: "廠牌車款未選擇",
          })}
          colon={false}
          label={<span className='text-16'>廠牌車款</span>}
          required
        >
          <div>
            <Select
              {...register("brandId", {
                required: "廠牌未選擇",
              })}
              dropdownStyle={{ zIndex: 0 }}
              bordered={false}
              placeholder='請選擇廠牌'
              value={brandId}
              onChange={(v) => {
                setValue("brandId", v);
                setValue("kindId", undefined);
                setValue("myid", undefined);
                trigger();
              }}
              style={{
                border: "1PX solid #bbb",
                borderRadius: "4px",
                width: "268px",
                fontSize: "16px",
                marginRight: "8px",
              }}
            >
              {brandData?.map((item) => (
                <Select.Option key={item.id} value={item.id}>
                  {item.name}
                </Select.Option>
              ))}
            </Select>
            <Select
              {...register("kindId", {
                required: "車款未選擇",
              })}
              dropdownStyle={{ zIndex: 0 }}
              bordered={false}
              placeholder='請選擇車款'
              value={kindId}
              onClick={() => {
                if (!brandId) {
                  message.error("請先選擇廠牌");
                }
              }}
              onChange={(v) => {
                setValue("kindId", v);
                setValue("myid", undefined);
                trigger();
              }}
              style={{
                border: "1PX solid #bbb",
                borderRadius: "4px",
                width: "268px",
                fontSize: "16px",
                marginLeft: "12px",
              }}
            >
              {kindData?.map((item) => (
                <Select.Option key={item.kindId} value={item.kindId}>
                  {item.kindName}
                </Select.Option>
              ))}
            </Select>
          </div>
        </Form.Item>
        <Form.Item
          colon={false}
          label={
            <SplitText width={64} cls='text-16' label='車型'>
              車型
            </SplitText>
          }
        >
          <Select
            {...register("myid")}
            bordered={false}
            dropdownStyle={{ zIndex: 0 }}
            placeholder='請選擇車型'
            value={myid}
            onClick={() => {
              if (!kindId) {
                message.error("請先選擇廠牌車款");
              }
            }}
            onChange={(v) => {
              setValue("myid", v);
              trigger("myid");
            }}
            style={{ border: "1PX solid #bbb", borderRadius: "4px", width: "268px", fontSize: "16px" }}
          >
            {modelData?.map((item) => (
              <Select.Option key={item.myid} value={item.myid}>
                {item.modelName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item colon={false} label={<span className='text-16 h-32 leading-32'>車輛顏色</span>}>
          <div className='flex-1 flex gap-x-20 gap-y-10 items-center flex-wrap'>
            <Select
              placeholder='請選擇顏色'
              {...register("color")}
              bordered={false}
              dropdownStyle={{ zIndex: 0 }}
              value={color}
              onChange={(v) => {
                setValue("color", v);
                setValue("otherColor", "");
                setFormState({ colorText: "" });
                trigger();
              }}
              placement='bottomLeft'
              style={{
                border: "1PX solid #bbb",
                borderRadius: "4px",
                width: "268px",
                fontSize: "16px",
                marginLeft: "-1px",
              }}
            >
              {data?.options?.color?.map((item, index) => (
                <Select.Option key={item.value} value={item.value}>
                  <div className='flex gap-8 items-center'>
                    {item?.colorValue && (
                      <span
                        className='flex w-24 h-24 rounded-4 border-1 border-solid'
                        style={{
                          background: item?.colorValue,
                          borderColor: item?.colorValue === "#FFFFFF" ? "#bbb" : "#fff",
                        }}
                      ></span>
                    )}
                    {item.name}
                  </div>
                </Select.Option>
              ))}
            </Select>
            {String(color) === "13" && (
              <div className='w-268 ml-4'>
                <Input
                  {...register("otherColor")}
                  name='otherColor'
                  maxLength={14}
                  style={{
                    border: "1PX solid #bbb",
                    borderRadius: "4px",
                    height: "34px",
                    boxSizing: "border-box",
                    fontSize: "16px",
                  }}
                  placeholder='請輸入車輛顏色'
                  // 包裹在radio中的的value輸入中文時會中斷，臨時使用一個state替代一下
                  value={colorText}
                  onChange={(e) => {
                    setFormState({ colorText: e.target.value });
                    setValue("otherColor", e.target.value);
                    trigger();
                  }}
                />
              </div>
            )}
          </div>
        </Form.Item>
      </div>

      <div className='flex flex-col mt-20 rounded-4 bg-[#fff]'>
        <span className='font-semibold text-20 text-[#333] my-30 ml-[150px]'>2.價格資訊</span>
        <Form.Item colon={false} required label={<span className='text-16'>購車地區</span>}>
          <RegionSelect
            placeholder='請選擇購車地區'
            style={{ border: "1PX solid #bbb", borderRadius: "4px", width: "268px", fontSize: "16px" }}
            {...register("regionId", {
              required: "購車地區未選擇",
            })}
            value={regionId}
            onChange={(v) => {
              setValue("regionId", v);
              trigger();
            }}
          />
        </Form.Item>
        <Form.Item colon={false} label={<span className='text-16'>購車時間</span>}>
          <Select
            {...register("buyCarTime")}
            bordered={false}
            dropdownStyle={{ zIndex: 0 }}
            value={buyCarTime}
            onChange={(v) => {
              setValue("buyCarTime", v);
              trigger();
            }}
            placeholder='請選擇購車時間'
            style={{ border: "1PX solid #bbb", borderRadius: "4px", width: "268px", fontSize: "16px" }}
          >
            {data?.options?.buyCarTime?.map?.((item) => (
              <Select.Option style={{ borderColor: "red" }} key={item.value} value={item.value}>
                {item.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item colon={false} label={<span className='text-16'>購車預算</span>}>
          <div className='w-268'>
            <Input
              {...register("price")}
              name='price'
              style={{ border: "1PX solid #bbb", borderRadius: "4px", width: "268px", fontSize: "16px" }}
              placeholder='請輸入預算'
              value={price}
              type='tel'
              onChange={(e) => {
                setValue("price", e.target.value);
                trigger();
              }}
              suffix='萬'
            />
          </div>
        </Form.Item>
        <Form.Item colon={false} label={<span className='text-16 h-32 leading-32'>付款方式</span>}>
          <div className='w-[450px] flex gap-20 cursor-pointer items-center'>
            {data?.options?.paymentType?.map((i) => (
              <div
                key={i.value}
                className='flex items-center'
                onClick={() => {
                  setValue("paymentType", i.value);
                  trigger();
                }}
              >
                {paymentType === i.value ? (
                  <CheckedFillIcon size={40} fill='#3264ff' />
                ) : (
                  <RoundIcon size={40} fill='#999' />
                )}
                <span className='text-16 text-[#333] ml-8'>{i.name}</span>
              </div>
            ))}
          </div>
        </Form.Item>
      </div>

      <div className='flex flex-col mt-20 rounded-4 bg-[#fff] pl-[150px]'>
        <span className='font-semibold text-20 text-[#333] my-30'>
          3.其他補充<span className='text-16 text-[#999] font-normal'>(選填)</span>
        </span>
        <div className='flex flex-col gap-20 ml-[74px]'>
          {data?.options?.other?.map((i) => (
            <div className='flex flex-col gap-10' key={i.value}>
              <div className='flex gap-8 cursor-pointer'>
                <span className='text-16  leading-22 text-[#333]'>{i.name}</span>
              </div>
              <div className='flex flex-col'>
                {i.value === 1 && (
                  <div className='relative'>
                    <Input.TextArea
                      rows={6}
                      autoSize={false}
                      maxLength={2000}
                      style={{
                        width: "560px",
                        height: "164px",
                        fontSize: "16px",
                        border: "1PX solid #bbb",
                        borderRadius: "4px",
                      }}
                      value={insuranceText}
                      onChange={(e) => {
                        setFormState({ insuranceText: e.target.value });
                      }}
                      placeholder={`例如：
業務人員協助投保(實報實銷)
女性(30~60歲)
乙式全險
4萬上下
PS:若保險自理，折扣少5千`}
                    />
                    <div className='absolute text-14 flex-col text-[#333] pt-22 pl-40 justify-start left-[560px] top-0 w-[275px] h-[170px] bg-[url(https://photo.8891.com.tw/2024/11/01/1730449229163011.png)] bg-contain'>
                      <p>例如：</p>
                      <p>業務人員協助投保(實報實銷)</p>
                      <p>女性(30~60歲)</p>
                      <p>乙式全險</p>
                      <p>4萬上下</p>
                      <p>PS:若保險自理，折扣少5千</p>
                    </div>
                  </div>
                )}
                {i.value === 2 && (
                  <Input
                    placeholder={`例如:共2萬元（包含領牌費）`}
                    value={returnFeeText}
                    style={{
                      width: "560px",
                      fontSize: "16px",
                      border: "1PX solid #bbb",
                      borderRadius: "4px",
                    }}
                    onChange={(e) => {
                      setFormState({ returnFeeText: e.target.value });
                    }}
                  />
                )}
                {i.value === 3 && (
                  <div className='relative'>
                    <Input.TextArea
                      rows={6}
                      autoSize={false}
                      maxLength={2000}
                      style={{
                        width: "560px",
                        height: "164px",
                        fontSize: "16px",
                        marginBottom: "20px",
                        border: "1PX solid #bbb",
                        borderRadius: "4px",
                      }}
                      placeholder={`例如：
自費
1.原廠360度環景 11932元
2.3M隔熱紙(前檔MB45+其他8803MT) 5023元
3.HUD抬頭顯示器  33239元
4.防盜升級 12189元`}
                      value={equipmentText}
                      onChange={(e) => {
                        setFormState({ equipmentText: e.target.value });
                      }}
                    />
                    <div className='absolute text-14 flex-col text-[#333] pt-20 pl-40 justify-start left-[560px] top-0 w-[337px] h-[170px] bg-[url(https://photo.8891.com.tw/2024/09/23/1727062049791395.png)] bg-contain'>
                      <p>例如：</p>
                      <p>自費</p>
                      <p>1.原廠360度環景 11932元</p>
                      <p>2.3M隔熱紙(前檔MB45+其他8803MT) 5023元</p>
                      <p>3.HUD抬頭顯示器 33239元</p>
                      <p>4.防盜升級 12189元</p>
                    </div>
                  </div>
                )}
                {i.value === 4 && (
                  <Input.TextArea
                    rows={4}
                    autoSize={false}
                    maxLength={2000}
                    style={{
                      width: "560px",
                      height: "82px",
                      border: "1PX solid #bbb",
                      borderRadius: "4px",
                      fontSize: "16px",
                    }}
                    placeholder={`請輸入備註`}
                    value={remarkText}
                    onChange={(e) => {
                      setFormState({ remarkText: e.target.value });
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        <span className='text-12 text-[#999] mt-20 mb-40 ml-[74px]'>以上內容將會公開展示，請注意遮擋個人資料</span>
      </div>

      <div className='flex flex-col mt-20 pb-40 rounded-4 bg-[#fff]'>
        <span className='font-semibold text-20 text-[#333] my-30 ml-[150px]'>
          4.行動電話<span className='text-16 text-[#999] font-normal'>(便於業代聯絡您報價)</span>
        </span>
        <Form.Item colon={false} required label={<SplitText cls='text-16' label='稱呼' width={60} />}>
          <div className='w-[450px]'>
            <Controller
              name='name'
              rules={{
                required: "請輸入中文稱呼",
                pattern: {
                  value: /^[a-zA-Z\u4e00-\u9fa5]+$/,
                  message: "請輸入正確稱呼",
                },
              }}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  maxLength={25}
                  placeholder='請輸入中文稱呼，例如:陳先生'
                  style={{ border: "1PX solid #bbb", borderRadius: "4px", width: "300px", fontSize: "16px" }}
                  onBlur={(e) => {
                    const val = e.target.value;
                    setValue("name", val?.replace(/[^a-zA-Z\u4e00-\u9fa5]/g, ""));
                    trigger();
                  }}
                />
              )}
            />
          </div>
        </Form.Item>
        <div className='flex gap-16 ml-[216px]'>
          <div className='w-[322px]'>
            <Form.Item colon={false} required label={<span className='text-16'>行動電話</span>}>
              <div className='w-200'>
                <Input
                  {...register("mobile", {
                    required: "行動電話未填寫",
                  })}
                  maxLength={10}
                  style={{ border: "1PX solid #bbb", borderRadius: "4px", width: "300px", fontSize: "16px" }}
                  placeholder='個資保密，業代看不到您真實行動電話'
                  value={mobile}
                  type='tel'
                  onChange={(e) => {
                    console.log(e.target.value, "..e.target.value");
                    setValue("mobile", e.target.value);
                    setValue("codeVerify", 1);
                    trigger("mobile");
                    trigger("codeVerify");
                  }}
                />
              </div>
            </Form.Item>
          </div>
          {codeVerify === 1 && (
            <div className='w-[500px]'>
              <Form.Item colon={false} required label={<span className='text-16'>驗 證 碼</span>}>
                <div className='w-full flex'>
                  <div className='w-200 mr-20'>
                    <Input
                      {...register("code")}
                      style={{ border: "1PX solid #bbb", borderRadius: "4px", width: "200px", fontSize: "16px" }}
                      placeholder='請輸入驗證碼'
                      value={code}
                      onChange={(e) => {
                        setValue("code", e.target.value);
                        trigger("code");
                      }}
                    />
                  </div>
                  <SendVerificationCode
                    style={{
                      marginLeft: "20px",
                      float: "right",
                      position: "absolute",
                      right: 0,
                      bottom: 0,
                      fontSize: "16px",
                    }}
                    account={mobile}
                    onCallBack={() => {}}
                    type='8'
                  />
                </div>
              </Form.Item>
            </div>
          )}
        </div>
        <div className='mb-12 w-full ml-[306px] flex flex-col'>
          <div
            className='w-[440px] h-[47px] leading-[47px] bg-[#3264ff] rounded-4 text-18 text-[#fff] text-center cursor-pointer -ml-10'
            onClick={debounce(() => {
              trigger();
              if (Object.values(errors).length) {
                Object.values(errors)?.forEach((item, index) => {
                  if (index === 0) {
                    message.error(item?.message);
                  }
                });
              } else if (String(color) === "13" && !getValues()?.otherColor) {
                message.error("請填寫車輛顏色");
              } else {
                onSubmit(getValues());
              }
            }, 400)}
          >
            提交
          </div>
          <div className='mt-12 -ml-[17px] w-[440px]'>
            <Agreement
              {...register("agree", { required: "請勾選協議", validate: (value) => value === 1 || "請勾選協議" })}
              onChange={(value) => {
                setValue("agree", Number(value));
                trigger("agree");
              }}
              showMenu={true}
            />
          </div>
        </div>
      </div>
    </Form>
  );
};

export default SeekFormComp;
